import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { getInstanceLocale } from '../../instance/instance.reducer';
import { ByKey, NormalizedSingle } from '@industi/ngx-common';
import { HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CarbonCommissionTaskAddAction,
  CarbonCommissionTaskAddActionTypes,
  CarbonCommissionTaskAddFailAction,
  CarbonCommissionTaskAddSuccessAction
} from './actions/add.actions';
import { CarbonCommissionTaskService } from './commission-task.service';
import {
  CarbonCommissionTaskEditAction,
  CarbonCommissionTaskEditActionTypes,
  CarbonCommissionTaskEditFailAction,
  CarbonCommissionTaskEditSuccessAction
} from './actions/edit.actions';
import { ResponseStatus } from '../../../shared/enums/response-status.enum';
import { LibSnackbarComponent } from '@industi/ngx-modules';
import { AppGlobal } from '../../../shared/app-global';
import {
  CarbonCommissionTaskLoadDetailsAction,
  CarbonCommissionTaskLoadDetailsActionTypes,
  CarbonCommissionTaskLoadDetailsFailAction,
  CarbonCommissionTaskLoadDetailsSuccessAction
} from './actions/load-details.actions';
import {
  CarbonCommissionTaskDeleteAction,
  CarbonCommissionTaskDeleteActionTypes,
  CarbonCommissionTaskDeleteFailAction,
  CarbonCommissionTaskDeleteSuccessAction
} from './actions/delete.actions';
import { AlertUtil } from '../../../shared/utils/alert.util';

@Injectable()
export class CarbonCommissionTaskEffects {

  @Effect()
  loadDetails$ = this.actions$.pipe(
    ofType(CarbonCommissionTaskLoadDetailsActionTypes.request),
    map((action: CarbonCommissionTaskLoadDetailsAction) => action.payload.id),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([id, locale]) => this.service.details(locale, id).pipe(
      map((res: NormalizedSingle) => new CarbonCommissionTaskLoadDetailsSuccessAction({ id, res })),
      catchError((errors: HttpErrorResponse) => of(new CarbonCommissionTaskLoadDetailsFailAction({ id, errors })))
    ))
  );

  @Effect()
  add$ = this.actions$.pipe(
    ofType(CarbonCommissionTaskAddActionTypes.request),
    map((action: CarbonCommissionTaskAddAction) => action.payload),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([{ id, data }, locale]) => this.service.add(locale, data).pipe(
      map((res: NormalizedSingle) => new CarbonCommissionTaskAddSuccessAction({ res, id })),
      catchError((errors: HttpErrorResponse) => of(new CarbonCommissionTaskAddFailAction({ errors, id })))
    ))
  );

  @Effect()
  edit$ = this.actions$.pipe(
    ofType(CarbonCommissionTaskEditActionTypes.request),
    map((action: CarbonCommissionTaskEditAction) => action.payload),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([{ id, data }, locale]) => this.service.edit(locale, data, id).pipe(
      map((res: NormalizedSingle) => new CarbonCommissionTaskEditSuccessAction({ res, id })),
      catchError((errors: HttpErrorResponse) => of(new CarbonCommissionTaskEditFailAction({ errors, id })))
    ))
  );

  @Effect()
  delete$ = this.actions$.pipe(
    ofType(CarbonCommissionTaskDeleteActionTypes.request),
    map((action: CarbonCommissionTaskDeleteAction) => action.payload.id),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([id, locale]) => this.service.delete(locale, id).pipe(
      map(() => new CarbonCommissionTaskDeleteSuccessAction({ id })),
      catchError((errors: HttpErrorResponse) => of(new CarbonCommissionTaskDeleteFailAction({ id, errors })))
    ))
  );

  @Effect({ dispatch: false })
  crudFail$ = this.actions$.pipe(
    ofType(
      CarbonCommissionTaskAddActionTypes.requestFail,
      CarbonCommissionTaskEditActionTypes.requestFail,
    ),
    map((action: CarbonCommissionTaskAddFailAction | CarbonCommissionTaskEditFailAction) => action.payload.errors),
    tap((data: HttpErrorResponse) => {
      if (data?.status === ResponseStatus.BAD_REQUEST || data?.status === ResponseStatus.UNPROCESSABLE_ENTITY) {
        this.snackBar.openFromComponent(LibSnackbarComponent, {
          data: {
            message: AlertUtil.getControlTranslation(data.error.detail),
            type: 'error'
          },
          duration: AppGlobal.defaultSnackBarDuration
        });
      }
    })
  );

  constructor(
    private actions$: Actions,
    private store: Store<ByKey>,
    private service: CarbonCommissionTaskService,
    private snackBar: MatSnackBar
  ) {
  }
}
